import React from "react";
import { connect } from "react-redux";
import {
  Button,
  Paper,
  Divider,
  CssBaseline,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  Container,
  IconButton,
  InputAdornment,
} from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { Helmet } from "react-helmet";
import { Formik } from "formik";
import * as Yup from "yup";
import Logo from "../../components/logo/HomeLogo";
import { history } from "../../components/helpers/History";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { PatientActions } from "../../redux/actions/PatientActions";

const useStyles = (theme) => ({
  paper: {
    marginTop: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
    backgroundColor: "#fff",
    padding: theme.spacing(4),
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  typography: {
    margin: theme.spacing(3),
    fontFamily: ["Josefin Sans", "sans-serif"].join(","),
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  buttons: {
    display: "flex",
    justifyContent: "center",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
});

class PatientSignUp extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      user: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
        phoneNo: "",
      },
      submitted: false,
      showPassword: false,
      showConfirmPassword: false,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.registeredSuccessfully) {
      this.props.addNew();
      history.goBack();
    }
  }

  componentWillUnmount() {
    this.props.clearAll();
  }

  

  render() {
    const classes = this.props.classes;
    return (
      <>
        <Helmet>
          <title>RxRapidMad | Registration</title>
        </Helmet>
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <Paper className={classes.paper}>
            <Logo></Logo>
            <Formik
              initialValues={{
                firstName: this.state.user.firstName,
                lastName: this.state.user.lastName,
                email: this.state.user.email,
                password: this.state.user.password,
                confirmPassword: this.state.user.confirmPassword,
                phoneNo: this.state.user.phoneNo,
                userRole: "ROLE_PATIENT",
                status: "ACTIVE",
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string()
                  .email("Must be a valid email")
                  .max(100)
                  .required("Email is required"),
                firstName: Yup.string()
                  .max(30)
                  .required("First name is required"),
                lastName: Yup.string()
                  .max(30)
                  .required("Last name is required"),
                phoneNo: Yup.string()
                  .matches(/^[0-9]+$/, "Must be only digits")
                  .min(10, "Must be exactly 10 digits")
                  .max(10, "Must be exactly 10 digits")
                  .required("Phone number required"),
                password: Yup.string()
                  .max(30)
                  .matches(
                    /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
                    "Password must contain at least 8 characters, one uppercase, one number and one special case character"
                  )
                  .required("Password is required"),
                confirmPassword: Yup.string()
                  .max(30)
                  .required("Confirm password is required")
                  .oneOf([Yup.ref("password"), null], "Passwords must match"),
              })}
              onSubmit={(values, { setSubmitting }) => {
                this.setState({ submitted: true });
                this.props.submit(values);
              }}
            >
              {({
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                touched,
                values,
              }) => (
                <form
                  onSubmit={handleSubmit}
                  className={classes.form}
                  noValidate
                  name="form"
                >
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      color="textPrimary"
                      align="center"
                      variant="h2"
                      className={classes.typography}
                    >
                      Sign up
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        error={Boolean(touched.firstName && errors.firstName)}
                        fullWidth
                        helperText={touched.firstName && errors.firstName}
                        label="First Name"
                        name="firstName"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          this.setState({ submitted: false });
                          handleChange(e);
                        }}
                        value={values.firstName}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        error={Boolean(touched.lastName && errors.lastName)}
                        fullWidth
                        helperText={touched.lastName && errors.lastName}
                        label="Last Name"
                        name="lastName"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          this.setState({ submitted: false });
                          handleChange(e);
                        }}
                        value={values.lastName}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        error={Boolean(touched.email && errors.email)}
                        fullWidth
                        helperText={touched.email && errors.email}
                        label="Email | Username"
                        name="email"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          this.setState({ submitted: false });
                          handleChange(e);
                        }}
                        value={values.email}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <TextField
                        error={Boolean(touched.phoneNo && errors.phoneNo)}
                        fullWidth
                        helperText={touched.phoneNo && errors.phoneNo}
                        label="Phone No."
                        name="phoneNo"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          this.setState({ submitted: false });
                          handleChange(e);
                        }}
                        value={values.phoneNo}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type={this.state.showPassword ? "text" : "password"}
                        variant="outlined"
                        error={Boolean(touched.password && errors.password)}
                        helperText={touched.password && errors.password}
                        fullWidth
                        name="password"
                        label="Password"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          this.setState({ submitted: false });
                          handleChange(e);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => {
                                  this.setState({
                                    showPassword: !this.state.showPassword,
                                  });
                                }}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                }}
                                edge="end"
                              >
                                {this.state.showPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                        autoComplete="current-password"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type={
                          this.state.showConfirmPassword ? "text" : "password"
                        }
                        variant="outlined"
                        error={Boolean(
                          touched.confirmPassword && errors.confirmPassword
                        )}
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                        fullWidth
                        name="confirmPassword"
                        label="Confirm password"
                        onBlur={handleBlur}
                        onChange={(e) => {
                          this.setState({ submitted: false });
                          handleChange(e);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="toggle password visibility"
                                onClick={() => {
                                  this.setState({
                                    showConfirmPassword:
                                      !this.state.showConfirmPassword,
                                  });
                                }}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                }}
                                edge="end"
                              >
                                {this.state.showConfirmPassword ? (
                                  <Visibility />
                                ) : (
                                  <VisibilityOff />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <div className={classes.buttons}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      className={classes.button}
                      disabled={this.state.submitted}
                    >
                      Sign up
                    </Button>
                  </div>
                  <Divider variant="middle" className={classes.divider} />
                  <Grid container justify="flex-end">
                    <Grid item>
                      <Link href="/login" variant="body2">
                        Already have an account? Sign in
                      </Link>
                    </Grid>
                  </Grid>
                </form>
              )}
            </Formik>
          </Paper>
        </Container>
      </>
    );
  }
}

function mapStateToProps(state) {
  const registeredSuccessfully = state.patient.registeredSuccessfully;
  return { registeredSuccessfully };
}

const mapDispatchToProps = (dispatch) => {
  return {
    submit: (user) => {
      dispatch(PatientActions.register(user));
    },
    addNew: (user) => {
      dispatch(PatientActions.addNewPatient(user));
    },clearAll : ()=>{
      dispatch(PatientActions.clearAll());
    }
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(useStyles, { withTheme: true })(PatientSignUp));
